import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule, DatePipe } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { environment } from '../environments/environment';

import { AppRoutingModule } from './app-routing.module';

import { AppComponent } from './app.component';
import { LoginComponent } from './components/auth/login/login.component';
import { TwoStepVerificationComponent } from './components/auth/two-step-verification/two-step-verification.component';
import { PageNotFoundComponent } from './components/page-not-found/page-not-found.component';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { SoapNotesComponent } from './components/soap-notes/soap-notes.component';

import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { SharedModule } from './shared/shared.module';

import { SideNavComponent } from './components/side-nav/side-nav.component';
import { BottomNavComponent } from './components/bottom-nav/bottom-nav.component';
import { SoapConfigurationComponent } from './components/soap-configuration/soap-configuration.component';
import { ForgotPasswordComponent } from './components/auth/forgot-password/forgot-password.component';

import { CustomIconService } from './services/customIons/custom-icon.service';
import { RequestInterceptorService } from './services/http/request-interceptor/request-interceptor.service';
import { ResponseInterceptorService } from './services/http/response-interceptor/response-interceptor.service';
import { HistoricalDataComponent } from './components/historical-data/historical-data.component';
import { PatientAppointmentListComponent } from './components/patient-appointment-list/patient-appointment-list.component';
import { AppointmentCardViewComponent } from './components/appointment-card-view/appointment-card-view.component';
import {
  SpeechToTextModule,
  SpeechToTextService,
} from '@privatelibdev/speech-to-text';

import { PatientBannerComponent } from './components/patient-banner/patient-banner.component';
import { SoapServiceService } from './services/soap-service/soap-service.service';
import { SnomedCodesDailogComponent } from './components/snomed-codes-dailog/snomed-codes-dailog.component';
import { ExpansionPanelComponent } from './components/expansion-panel/expansion-panel.component';
import { NgxAuthzModule } from '@privatelib/ngx18-authz-dev';
import { ConfirmDailogComponent } from './components/confirm-dailog/confirm-dailog.component';
import { SideNavMenuItemsComponent } from './components/side-nav-menu-items/side-nav-menu-items.component';
import { AppointmentListComponent } from './components/appointment-list/appointment-list.component';
import { PatientDashboardComponent } from './components/patient-dashboard/patient-dashboard.component';
import { AppointmentCardComponent } from './components/appointment-list/appointment-card/appointment-card.component';
import { BookAppointmentComponent } from './components/book-appointment/book-appointment.component';
import { NgHttpLoaderComponent, pendingRequestsInterceptor$ } from 'ng-http-loader';
import { ChangePasswordComponent } from './components/auth/change-password/change-password.component';
import { CustomSelectComponent } from './components/custom-select/custom-select.component';
import { NotificationBannerComponent } from './components/notification-banner/notification-banner.component';
import {
  ConsultationComponent,
  ConsultationTabViewComponent,
  ConsultationGridViewComponent,
  ConsultationSummaryTabComponent,
  ConsultationTasksComponent,
  ConsultationRecommendationsComponent,
  ConsultationMedicationsComponent,
  ConsultationClassificationComponent,
  ConsultationAccEligibilityComponent
} from "./components/consultation/";
import { RecordTranscriptionComponent } from "./components/record-transcription/record-transcription.component";
import { LicComponent } from './components/lic/lic.component';
import { TranscriptionModalComponent } from './components/appointment-list/transcription-modal/transcription-modal.component';
import { NgbModule, NgbPopoverModule } from '@ng-bootstrap/ng-bootstrap';
import { LookupService } from './services/lookup/lookup.service';
import { hasAuthTokens } from './util/auth-tokens.util';
import { PatientSearchModalComponent } from './components/patient-search-modal/patient-search-modal.component';
import { BottomSheetComponent } from './components/bottom-sheet/bottom-sheet.component';
import { FacilitySelectionComponent } from './components/facility-selection/facility-selection.component';
import { CopyrightComponent } from './components/copyright/copyright.component';
import { ResetPasswordComponent } from './components/auth/reset-password/reset-password.component';
import { TemplateCardComponent } from './components/manage-template/template-card/template-card.component';
import { PracticeSignupComponent } from './components/practice-signup/practice-signup.component';
import { PatientConsentComponent } from './components/patient-consent/patient-consent.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { LayoutModule } from '@angular/cdk/layout';
import { CdmPlanModalComponent } from './shared/cdm-plan-modal/cdm-plan-modal.component';
import { GlobalHttpLoaderComponent } from './shared/global-http-loader/global-http-loader.component';
import { ReauthPopupComponent } from './components/auth/reauth-popup/reauth-popup.component';
import { ViewTemplateComponent } from './components/manage-template/view-template/view-template.component';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from "ng-recaptcha-2";
import { LicLogoffScreenComponent } from './components/lic-logoff-screen/lic-logoff-screen.component';
import { SpeechToTextChunkedComponent } from './components/speech-to-text-chunked/speech-to-text-chunked.component';
import { TranscriptOverlayComponent } from './components/transcript-overlay/transcript-overlay.component';

export function initializeSpeechSDK(lookupService: LookupService) {
  return async () => {
    if (hasAuthTokens()) {
      await lookupService.initSpeechService();
    } else {
      const fragment = location.hash.substring(1);
      if (fragment) {
        const tokenSet = fragment.split('&');
        const tokens = Object.fromEntries(tokenSet.map((e) => e.split("=")));
        if (tokens?.idToken && tokens?.authToken) {
          localStorage.setItem('idToken', tokens?.idToken);
          localStorage.setItem('authToken', tokens?.authToken);
          await lookupService.initSpeechService();
        }
      } else if (location.search.includes('sdk')) {
        const tokenSet = location.search.substring(1).split('&');
        const tokens = Object.fromEntries(tokenSet.map((e) => e.split("=")));
        try {
          const config = JSON.parse(decodeURIComponent(tokens?.sdk));
          SpeechToTextService.init(config);
        } catch (error) {
          console.error(error);
        }
      }
    }
  }
}

export const httpInterceptorProviders = [
  {
    provide: HTTP_INTERCEPTORS,
    useClass: RequestInterceptorService,
    multi: true,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: ResponseInterceptorService,
    multi: true,
  },
];

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    TwoStepVerificationComponent,
    SideNavComponent,
    PageNotFoundComponent,
    BottomNavComponent,
    DashboardComponent,
    SoapNotesComponent,
    SoapConfigurationComponent,
    ForgotPasswordComponent,
    HistoricalDataComponent,
    PatientBannerComponent,
    SnomedCodesDailogComponent,
    PatientAppointmentListComponent,
    AppointmentCardViewComponent,
    ExpansionPanelComponent,
    ConfirmDailogComponent,
    SideNavMenuItemsComponent,
    AppointmentListComponent,
    PatientDashboardComponent,
    AppointmentCardComponent,
    BookAppointmentComponent,
    ChangePasswordComponent,
    CustomSelectComponent,
    NotificationBannerComponent,
    ConsultationComponent,
    RecordTranscriptionComponent,
    ConsultationTabViewComponent,
    ConsultationGridViewComponent,
    ConsultationSummaryTabComponent,
    LicComponent,
    TranscriptionModalComponent,
    PatientSearchModalComponent,
    BottomSheetComponent,
    FacilitySelectionComponent,
    CopyrightComponent,
    ResetPasswordComponent,
    TemplateCardComponent,
    PracticeSignupComponent,
    PatientConsentComponent,
    ConsultationTasksComponent,
    ConsultationRecommendationsComponent,
    ConsultationMedicationsComponent,
    ConsultationClassificationComponent,
    ConsultationAccEligibilityComponent,
    CdmPlanModalComponent,
    GlobalHttpLoaderComponent,
    LicLogoffScreenComponent,
    SpeechToTextChunkedComponent,
    TranscriptOverlayComponent
  ],
  bootstrap: [AppComponent], imports: [BrowserModule,
    BrowserAnimationsModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    AppRoutingModule,
    DragDropModule,
    ClipboardModule,
    SpeechToTextModule,
    SharedModule,
    NgxAuthzModule,
    NgHttpLoaderComponent,
    NgbModule,
    NgbPopoverModule,
    MatProgressSpinnerModule,
    LayoutModule,
    RecaptchaV3Module,
    ReauthPopupComponent,
    ViewTemplateComponent], providers: [
      {
        provide: 'environment', useValue: environment
      },
      { provide: RECAPTCHA_V3_SITE_KEY, useValue: environment.siteKey },
      httpInterceptorProviders,
      provideAnimationsAsync('noop'),
      provideAnimationsAsync(),
      SoapServiceService,
      DatePipe,
      {
        provide: APP_INITIALIZER,
        useFactory: initializeSpeechSDK,
        deps: [LookupService],
        multi: true
      },
      provideHttpClient(withInterceptorsFromDi(), withInterceptors([pendingRequestsInterceptor$]))
    ]
})
export class AppModule {
  constructor(private readonly customIconService: CustomIconService) {
    this.customIconService.registerCustomIcon();
  }
}
